/**
 * Feed logging sheet: a persistent two-side breast timer, a bottle form,
 * and a manual breast entry, plus editing of existing feed events.
 *
 * The in-progress timer session is written to IndexedDB (kv, ACTIVE_FEED_KEY)
 * on every state change, so it survives app restarts. Elapsed time for the
 * running side is derived from runningSince, never from an in-memory tick,
 * which keeps the clock honest across reloads. The Track screen reads the
 * same key to show a live "feeding in progress" card.
 */
/* eslint-disable react-refresh/only-export-components */
import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/i18n';
import { kvDelete, kvGet, kvSet } from '../lib/db';
import { newEventBase, useActions, useApp } from '../lib/store';
import { formatDuration, nowIso, toLocalIso } from '../lib/time';
import { displayToMl, ML_PER_OZ, mlToDisplay } from '../lib/units';
import type { BottleContents, BreastSide, FeedEvent, VolumeUnit } from '../lib/types';
import { Segmented, Sheet } from './ui';

// ── Persistent timer session ───────────────────────────────────────────────

export const ACTIVE_FEED_KEY = 'activeFeed';

export interface ActiveFeedSession {
  startedAt: string;
  startedSide: 'left' | 'right';
  leftSeconds: number;
  rightSeconds: number;
  runningSide: 'left' | 'right' | null;
  runningSince: string | null;
}

/** Wall-clock seconds since the session started. */
export function sessionElapsedSeconds(s: ActiveFeedSession, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(s.startedAt).getTime()) / 1000));
}

/** Accumulated seconds for one side, including the currently running stretch. */
function sideSeconds(s: ActiveFeedSession, side: 'left' | 'right', now: Date): number {
  const base = side === 'left' ? s.leftSeconds : s.rightSeconds;
  if (s.runningSide === side && s.runningSince) {
    const extra = Math.floor((now.getTime() - new Date(s.runningSince).getTime()) / 1000);
    return base + Math.max(0, extra);
  }
  return base;
}

/** Fold the running stretch into the side totals and stop the clock. */
function foldRunning(s: ActiveFeedSession, now: Date): ActiveFeedSession {
  if (!s.runningSide || !s.runningSince) {
    return { ...s, runningSide: null, runningSince: null };
  }
  const extra = Math.max(
    0,
    Math.round((now.getTime() - new Date(s.runningSince).getTime()) / 1000),
  );
  return {
    ...s,
    leftSeconds: s.leftSeconds + (s.runningSide === 'left' ? extra : 0),
    rightSeconds: s.rightSeconds + (s.runningSide === 'right' ? extra : 0),
    runningSide: null,
    runningSince: null,
  };
}

// ── Bottle amount stepper bounds (in display units) ────────────────────────

const BOTTLE_STEP: Record<VolumeUnit, number> = { oz: 0.25, ml: 5 };
const BOTTLE_MAX: Record<VolumeUnit, number> = { oz: 12, ml: 360 };
const BOTTLE_DEFAULT: Record<VolumeUnit, number> = { oz: 2, ml: 60 };

function clampAmount(value: number, unit: VolumeUnit): number {
  return Math.min(BOTTLE_MAX[unit], Math.max(BOTTLE_STEP[unit], value));
}

/** ml -> display-unit number for prefilling the stepper. */
function mlToAmount(ml: number, unit: VolumeUnit): number {
  return unit === 'ml' ? Math.round(ml) : Math.round((ml / ML_PER_OZ) * 4) / 4;
}

function parseMin(s: string): number {
  const n = Math.round(Number(s));
  return Number.isFinite(n) && n > 0 ? Math.min(n, 600) : 0;
}

function breastSideOf(leftSeconds: number, rightSeconds: number, fallback: BreastSide): BreastSide {
  if (leftSeconds > 0 && rightSeconds > 0) return 'both';
  if (leftSeconds > 0) return 'left';
  if (rightSeconds > 0) return 'right';
  return fallback;
}

// ── Component ──────────────────────────────────────────────────────────────

type FeedMode = 'timer' | 'bottle' | 'manual';

const inputClass =
  'w-full rounded-2xl border border-line bg-surface-2 px-4 py-3 text-ink outline-none focus:border-primary';

export default function FeedSheet(props: {
  open: boolean;
  onClose: () => void;
  editing?: FeedEvent;
}) {
  const { open, onClose, editing } = props;
  const t = useT();
  const { device, settings } = useApp();
  const { saveEvent, deleteEvent } = useActions();
  const unit = settings.volumeUnit;

  const [mode, setMode] = useState<FeedMode>('timer');
  const [session, setSession] = useState<ActiveFeedSession | null>(null);
  const [now, setNow] = useState(() => new Date());

  // Shared fields (bottle + manual)
  const [timeStr, setTimeStr] = useState('');
  const [note, setNote] = useState('');
  // Bottle
  const [amount, setAmount] = useState(BOTTLE_DEFAULT[unit]);
  const [contents, setContents] = useState<BottleContents>('breastmilk');
  // Manual breast
  const [leftMinStr, setLeftMinStr] = useState('');
  const [rightMinStr, setRightMinStr] = useState('');

  // Prefill values at open time, to detect what the user actually changed.
  const initRef = useRef({ time: '', leftMin: 0, rightMin: 0, amount: 0 });

  // Initialize on every open (and whenever a different event is edited).
  useEffect(() => {
    if (!open) return;
    if (editing) {
      // Convert through Date so the prefill shows THIS device's local time;
      // slicing the raw string would replay the recording device's wall
      // clock and silently shift the event when saved across timezones.
      const time0 = toLocalIso(new Date(editing.at)).slice(0, 16);
      const lm = Math.round((editing.leftSeconds ?? 0) / 60);
      const rm = Math.round((editing.rightSeconds ?? 0) / 60);
      // Prefill the stored amount unclamped, so an out-of-range original
      // is never silently rewritten by a note-only edit.
      const amount0 =
        editing.amountMl && editing.amountMl > 0
          ? mlToAmount(editing.amountMl, unit)
          : BOTTLE_DEFAULT[unit];
      initRef.current = { time: time0, leftMin: lm, rightMin: rm, amount: amount0 };
      setMode(editing.method === 'bottle' ? 'bottle' : 'manual');
      setTimeStr(time0);
      setNote(editing.note ?? '');
      setContents(editing.contents ?? 'breastmilk');
      setAmount(amount0);
      setLeftMinStr(lm > 0 ? String(lm) : '');
      setRightMinStr(rm > 0 ? String(rm) : '');
    } else {
      const time0 = toLocalIso(new Date()).slice(0, 16);
      initRef.current = { time: time0, leftMin: 0, rightMin: 0, amount: BOTTLE_DEFAULT[unit] };
      setMode('timer');
      setTimeStr(time0);
      setNote('');
      setContents('breastmilk');
      setAmount(BOTTLE_DEFAULT[unit]);
      setLeftMinStr('');
      setRightMinStr('');
    }
    // Restore any in-progress timer session (survives restarts).
    let cancelled = false;
    void kvGet<ActiveFeedSession>(ACTIVE_FEED_KEY).then((s) => {
      if (!cancelled) setSession(s ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [open, editing, unit]);

  // One-second tick while the sheet is open, for the timer displays.
  useEffect(() => {
    if (!open) return;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [open]);

  // ── Timer actions ────────────────────────────────────────────────────────

  async function persistSession(next: ActiveFeedSession) {
    setSession(next);
    await kvSet(ACTIVE_FEED_KEY, next);
  }

  async function tapSide(side: 'left' | 'right') {
    const tapNow = new Date();
    if (!session) {
      const iso = toLocalIso(tapNow);
      await persistSession({
        startedAt: iso,
        startedSide: side,
        leftSeconds: 0,
        rightSeconds: 0,
        runningSide: side,
        runningSince: iso,
      });
      return;
    }
    if (session.runningSide === side) {
      await persistSession(foldRunning(session, tapNow));
      return;
    }
    await persistSession({
      ...foldRunning(session, tapNow),
      runningSide: side,
      runningSince: toLocalIso(tapNow),
    });
  }

  async function finishTimer() {
    if (!device || !session) return;
    const folded = foldRunning(session, new Date());
    const event: FeedEvent = {
      ...newEventBase(device),
      type: 'feed',
      method: 'breast',
      at: session.startedAt,
      endAt: nowIso(),
      leftSeconds: folded.leftSeconds,
      rightSeconds: folded.rightSeconds,
      startedSide: session.startedSide,
      side: breastSideOf(folded.leftSeconds, folded.rightSeconds, session.startedSide),
    };
    await saveEvent(event);
    await kvDelete(ACTIVE_FEED_KEY);
    setSession(null);
    onClose();
  }

  async function discardTimer() {
    if (!window.confirm(t.track.timer.confirmDiscard)) return;
    await kvDelete(ACTIVE_FEED_KEY);
    setSession(null);
    onClose();
  }

  // ── Form derivations ─────────────────────────────────────────────────────

  const timeValid = timeStr.length > 0 && !Number.isNaN(new Date(timeStr).getTime());

  const lm = parseMin(leftMinStr);
  const rm = parseMin(rightMinStr);
  // Preserve exact original seconds when the (rounded) minutes were not touched.
  const manualLeftSeconds =
    editing && lm === initRef.current.leftMin ? (editing.leftSeconds ?? lm * 60) : lm * 60;
  const manualRightSeconds =
    editing && rm === initRef.current.rightMin ? (editing.rightSeconds ?? rm * 60) : rm * 60;
  const manualValid = timeValid && manualLeftSeconds + manualRightSeconds > 0;
  const bottleValid = timeValid && amount > 0;

  // ── Save actions ─────────────────────────────────────────────────────────

  async function saveBottle() {
    if (!device || !bottleValid) return;
    const trimmed = note.trim();
    // Keep the exact original ml when the stepper was not touched, so a
    // note-only edit never rounds or clamps the stored amount.
    const amountMl =
      editing && amount === initRef.current.amount && editing.amountMl != null
        ? editing.amountMl
        : displayToMl(amount, unit);
    if (editing) {
      const at =
        timeStr === initRef.current.time ? editing.at : toLocalIso(new Date(timeStr));
      await saveEvent({
        ...editing,
        at,
        amountMl,
        contents,
        note: trimmed || undefined,
        updatedAt: nowIso(),
      });
    } else {
      const event: FeedEvent = {
        ...newEventBase(device),
        type: 'feed',
        method: 'bottle',
        at: toLocalIso(new Date(timeStr)),
        amountMl,
        contents,
        note: trimmed || undefined,
      };
      await saveEvent(event);
    }
    onClose();
  }

  async function saveManual() {
    if (!device || !manualValid) return;
    const trimmed = note.trim();
    const totalSeconds = manualLeftSeconds + manualRightSeconds;
    if (editing) {
      const timeChanged = timeStr !== initRef.current.time;
      const durationChanged =
        lm !== initRef.current.leftMin || rm !== initRef.current.rightMin;
      const at = timeChanged ? toLocalIso(new Date(timeStr)) : editing.at;
      const endAt =
        timeChanged || durationChanged
          ? toLocalIso(new Date(new Date(at).getTime() + totalSeconds * 1000))
          : editing.endAt;
      const side = durationChanged
        ? breastSideOf(manualLeftSeconds, manualRightSeconds, editing.side ?? 'both')
        : editing.side;
      await saveEvent({
        ...editing,
        at,
        endAt,
        leftSeconds: manualLeftSeconds,
        rightSeconds: manualRightSeconds,
        side,
        note: trimmed || undefined,
        updatedAt: nowIso(),
      });
    } else {
      const at = toLocalIso(new Date(timeStr));
      const event: FeedEvent = {
        ...newEventBase(device),
        type: 'feed',
        method: 'breast',
        at,
        endAt: toLocalIso(new Date(new Date(at).getTime() + totalSeconds * 1000)),
        leftSeconds: manualLeftSeconds,
        rightSeconds: manualRightSeconds,
        side: breastSideOf(manualLeftSeconds, manualRightSeconds, 'both'),
        note: trimmed || undefined,
      };
      await saveEvent(event);
    }
    onClose();
  }

  async function handleDelete() {
    if (!editing) return;
    if (!window.confirm(t.common.actions.confirmDelete)) return;
    await deleteEvent(editing.id);
    onClose();
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={editing ? t.track.feedSheet.editTitle : t.track.feedSheet.title}
    >
      <div className="space-y-5">
        {!editing && (
          <Segmented<FeedMode>
            value={mode}
            onChange={setMode}
            label={t.track.feedSheet.modeLabel}
            options={[
              { value: 'timer', label: t.track.feedSheet.modes.timer },
              { value: 'bottle', label: t.track.feedSheet.modes.bottle },
              { value: 'manual', label: t.track.feedSheet.modes.manual },
            ]}
          />
        )}

        {mode === 'timer' && !editing && (
          <div className="space-y-4">
            <p className="text-sm text-ink-soft">{t.track.timer.tapToStart}</p>
            <div className="grid grid-cols-2 gap-3">
              {(['left', 'right'] as const).map((side) => {
                const active = session?.runningSide === side;
                const secs = session ? sideSeconds(session, side, now) : 0;
                return (
                  <button
                    key={side}
                    type="button"
                    onClick={() => void tapSide(side)}
                    aria-pressed={active}
                    className={`flex min-h-36 flex-col items-center justify-center gap-1.5 rounded-3xl border transition-colors ${
                      active
                        ? 'animate-pulse-soft border-primary bg-primary text-on-primary'
                        : 'border-line bg-surface-2 text-ink'
                    }`}
                  >
                    <span className="text-sm font-bold uppercase tracking-wide opacity-80">
                      {t.common.sides[side]}
                    </span>
                    <span className="text-3xl font-bold tabular-nums">
                      {formatDuration(secs)}
                    </span>
                  </button>
                );
              })}
            </div>
            {session && (
              <p className="text-center text-sm text-ink-soft">
                {t.track.timer.session}:{' '}
                <span className="font-bold tabular-nums text-ink">
                  {formatDuration(sessionElapsedSeconds(session, now))}
                </span>
              </p>
            )}
            <button
              type="button"
              onClick={() => void finishTimer()}
              disabled={!session}
              className="btn-primary w-full py-3.5 text-base disabled:opacity-40"
            >
              {t.track.timer.finish}
            </button>
            {session && (
              <button
                type="button"
                onClick={() => void discardTimer()}
                className="w-full rounded-full py-3 font-bold text-bad"
              >
                {t.track.timer.discard}
              </button>
            )}
          </div>
        )}

        {mode === 'bottle' && (
          <div className="space-y-4">
            <div>
              <span className="mb-1.5 block text-sm font-bold text-ink-soft">
                {t.track.bottle.amount}
              </span>
              <div className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface-2 p-2">
                <button
                  type="button"
                  aria-label={t.track.bottle.decrease}
                  onClick={() => setAmount((a) => clampAmount(a - BOTTLE_STEP[unit], unit))}
                  className="btn-secondary h-12 w-12 shrink-0 text-2xl leading-none"
                >
                  −
                </button>
                <span className="text-2xl font-bold tabular-nums text-ink">
                  {mlToDisplay(displayToMl(amount, unit), unit)}
                </span>
                <button
                  type="button"
                  aria-label={t.track.bottle.increase}
                  onClick={() => setAmount((a) => clampAmount(a + BOTTLE_STEP[unit], unit))}
                  className="btn-secondary h-12 w-12 shrink-0 text-2xl leading-none"
                >
                  +
                </button>
              </div>
            </div>
            <div>
              <span className="mb-1.5 block text-sm font-bold text-ink-soft">
                {t.track.bottle.contents}
              </span>
              <Segmented<BottleContents>
                value={contents}
                onChange={setContents}
                label={t.track.bottle.contents}
                options={[
                  { value: 'breastmilk', label: t.common.contents.breastmilk },
                  { value: 'formula', label: t.common.contents.formula },
                  { value: 'mixed', label: t.common.contents.mixed },
                ]}
              />
            </div>
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-ink-soft">
                {t.track.fields.time}
              </span>
              <input
                type="datetime-local"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-ink-soft">
                {t.track.fields.note}
              </span>
              <input
                type="text"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t.track.fields.notePlaceholder}
                className={inputClass}
              />
            </label>
            <button
              type="button"
              onClick={() => void saveBottle()}
              disabled={!bottleValid}
              className="btn-primary w-full py-3.5 text-base disabled:opacity-40"
            >
              {t.common.actions.save}
            </button>
          </div>
        )}

        {mode === 'manual' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-ink-soft">
                  {t.track.manual.left}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={600}
                  value={leftMinStr}
                  onChange={(e) => setLeftMinStr(e.target.value)}
                  placeholder="0"
                  className={inputClass}
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-ink-soft">
                  {t.track.manual.right}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={600}
                  value={rightMinStr}
                  onChange={(e) => setRightMinStr(e.target.value)}
                  placeholder="0"
                  className={inputClass}
                />
              </label>
            </div>
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-ink-soft">
                {t.track.fields.startTime}
              </span>
              <input
                type="datetime-local"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-ink-soft">
                {t.track.fields.note}
              </span>
              <input
                type="text"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t.track.fields.notePlaceholder}
                className={inputClass}
              />
            </label>
            <button
              type="button"
              onClick={() => void saveManual()}
              disabled={!manualValid}
              className="btn-primary w-full py-3.5 text-base disabled:opacity-40"
            >
              {t.common.actions.save}
            </button>
          </div>
        )}

        {editing && (
          <button
            type="button"
            onClick={() => void handleDelete()}
            className="w-full rounded-full bg-bad-soft py-3 font-bold text-bad"
          >
            {t.common.actions.delete}
          </button>
        )}
      </div>
    </Sheet>
  );
}
